/**
 * Centralized Toast Message System with i18next
 *
 * This file contains all toast and error messages used throughout the application.
 * Messages are organized by category using TypeScript enums for type safety.
 * Translations are managed through i18next.
 *
 * Usage:
 * import { getFileUploadError, FileUploadError } from '@/constants/toast-messages';
 *
 * toast.error(getFileUploadError(FileUploadError.INVALID_TYPE_UPLOAD));
 * toast.error(getFileUploadError(FileUploadError.UNSUPPORTED_TYPE, { fileType: 'txt', supportedTypes: 'PDF, PNG' }));
 */

import i18next from "i18next";

// ============================================================================
// Message Key Enums
// ============================================================================

export enum FileUploadError {
  INVALID_TYPE_UPLOAD = "INVALID_TYPE_UPLOAD",
  INVALID_TYPE_PASTE = "INVALID_TYPE_PASTE",
  DUPLICATE_FILES = "DUPLICATE_FILES",
  UNSUPPORTED_TYPE = "UNSUPPORTED_TYPE",
  TOO_MANY_IMAGES = "TOO_MANY_IMAGES",
}

export enum ConnectionError {
  BACKEND_FAILED = "BACKEND_FAILED",
}

export enum GeneralError {
  GENERIC = "GENERIC",
}

export enum ToolExecutionError {
  EDIT_FAILED = "EDIT_FAILED",
  REJECT_FAILED = "REJECT_FAILED",
  APPROVE_FAILED = "APPROVE_FAILED",
}

export enum ToolExecutionSuccess {
  EDIT_SUCCESS = "EDIT_SUCCESS",
  REJECT_SUCCESS = "REJECT_SUCCESS",
  APPROVE_SUCCESS = "APPROVE_SUCCESS",
}

export enum DocumentError {
  CANNOT_SAVE = "CANNOT_SAVE",
  SAVE_FAILED = "SAVE_FAILED",
  SAVE_FAILED_NO_USER = "SAVE_FAILED_NO_USER",
  SAVE_FAILED_NO_PROJECT = "SAVE_FAILED_NO_PROJECT",
}

export enum ConversationError {
  CREATE_FAILED = "CREATE_FAILED",
  NAME_UPDATE_FAILED = "NAME_UPDATE_FAILED",
}

export enum RecordingError {
  FAILED = "FAILED",
  UPLOAD_FAILED = "UPLOAD_FAILED",
}

export enum UserError {
  NOT_FOUND = "NOT_FOUND",
}

export enum SuccessMessage {
  PROJECT_DELETED = "PROJECT_DELETED",
}

export enum DeleteError {
  PROJECT_FAILED = "PROJECT_FAILED",
}

// ============================================================================
// Helper Functions (Now using i18next)
// ============================================================================

/**
 * Type-safe helper to get file upload error messages
 */
export const getFileUploadError = (
  key: FileUploadError,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:fileUpload.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get connection error messages
 */
export const getConnectionError = (
  key: ConnectionError,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:connection.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get general error messages
 */
export const getGeneralError = (
  key: GeneralError,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:generic.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get tool execution error messages
 */
export const getToolExecutionError = (
  key: ToolExecutionError,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:toolExecution.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get tool execution success messages
 */
export const getToolExecutionSuccess = (
  key: ToolExecutionSuccess,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:toolExecution.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get success messages
 */
export const getSuccessMessage = (
  key: SuccessMessage,
  variables?: Record<string, string>,
) => {
  const translationKey = `toasts:success.${key.toLowerCase()}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get document error messages
 */
export const getDocumentError = (
  key: DocumentError,
  variables?: Record<string, string>,
) => {
  const keyMap: Record<DocumentError, string> = {
    [DocumentError.CANNOT_SAVE]: "cannotSave",
    [DocumentError.SAVE_FAILED]: "saveFailed",
    [DocumentError.SAVE_FAILED_NO_USER]: "saveFailedNoUser",
    [DocumentError.SAVE_FAILED_NO_PROJECT]: "saveFailedNoProject",
  };
  const translationKey = `toasts:document.${keyMap[key]}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get conversation error messages
 */
export const getConversationError = (
  key: ConversationError,
  variables?: Record<string, string>,
) => {
  const keyMap: Record<ConversationError, string> = {
    [ConversationError.CREATE_FAILED]: "createFailed",
    [ConversationError.NAME_UPDATE_FAILED]: "nameUpdateFailed",
  };
  const translationKey = `toasts:conversation.${keyMap[key]}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get recording error messages
 */
export const getRecordingError = (
  key: RecordingError,
  variables?: Record<string, string>,
) => {
  const keyMap: Record<RecordingError, string> = {
    [RecordingError.FAILED]: "failed",
    [RecordingError.UPLOAD_FAILED]: "uploadFailed",
  };
  const translationKey = `toasts:recording.${keyMap[key]}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get user error messages
 */
export const getUserError = (
  key: UserError,
  variables?: Record<string, string>,
) => {
  const keyMap: Record<UserError, string> = {
    [UserError.NOT_FOUND]: "notFound",
  };
  const translationKey = `toasts:user.${keyMap[key]}`;
  return i18next.t(translationKey, variables);
};

/**
 * Type-safe helper to get common error title
 */
export const getCommonError = () => {
  return i18next.t("common:common.error");
};

/**
 * Type-safe helper to get unknown error message
 */
export const getUnknownError = () => {
  return i18next.t("common:common.unknownError");
};

/**
 * Type-safe helper to get delete error messages
 */
export const getDeleteError = (
  key: DeleteError,
  variables?: Record<string, string>,
) => {
  const keyMap: Record<DeleteError, string> = {
    [DeleteError.PROJECT_FAILED]: "project_failed",
  };
  const translationKey = `toasts:delete.${keyMap[key]}`;
  return i18next.t(translationKey, variables);
};
